import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHandLibrary } from "../hooks/useHands";
import { useWorkspace } from "../hooks/useWorkspace";
import { useBindings, useHasLlmKey, type AgentBinding } from "../hooks/useAgents";

type AssignMode = "staff" | "agent";
type AgentTab = "library" | "connected";
type AgentSelection =
  | { kind: "hand"; handId: string; name: string }
  | { kind: "binding"; bindingId: string; name: string }
  | null;

interface StepEditModalProps {
  workspaceId: string;
  stepTitle: string;
  currentOwner: string;
  currentHandId: string | null;
  currentBindingId: string | null;
  isAutonomous: boolean;
  onSave: (update: {
    owner: string;
    autonomous: boolean;
    handId: string | null;
    bindingId: string | null;
  }) => void;
  onClose: () => void;
  saving?: boolean | undefined;
}

export function StepEditModal({
  workspaceId,
  stepTitle,
  currentOwner,
  currentHandId,
  currentBindingId,
  isAutonomous,
  onSave,
  onClose,
  saving,
}: StepEditModalProps) {
  const [mode, setMode] = useState<AssignMode>(isAutonomous ? "agent" : "staff");
  const [staffName, setStaffName] = useState(isAutonomous ? "" : currentOwner);
  const [selection, setSelection] = useState<AgentSelection>(() => {
    if (currentHandId) return { kind: "hand", handId: currentHandId, name: currentOwner };
    if (currentBindingId) return { kind: "binding", bindingId: currentBindingId, name: currentOwner };
    return null;
  });
  const [agentTab, setAgentTab] = useState<AgentTab>(() => currentBindingId ? "connected" : "library");
  // When the step already names an agent (templates ship owner=agent name)
  // but we don't have a handId yet, seed search with that name so the list
  // narrows to the assigned agent on open. Effect below auto-selects it
  // once the library query returns an unambiguous match.
  const seededNameLookup = isAutonomous && !currentHandId && !currentBindingId && currentOwner.trim().length > 0;
  const [search, setSearch] = useState(seededNameLookup ? currentOwner : "");
  const [vertical, setVertical] = useState("All");

  const ws = useWorkspace(workspaceId);
  const members = ws.data?.memberships ?? [];
  // Agent runs require a company LLM key. The modal still lets the user
  // pick an agent (selection is informational metadata for the step), but
  // we surface a hint so they aren't surprised when the step fails to run.
  const llmKey = useHasLlmKey(workspaceId);
  const showNoKeyHint = mode === "agent" && !llmKey.isLoading && !llmKey.hasDefault;
  const bindingsQ = useBindings(workspaceId);
  const bindings = (bindingsQ.data?.bindings ?? []).filter((b) => b.enabled);

  const companyLib = useHandLibrary(workspaceId, {
    scope: "company",
    search,
    vertical: vertical === "All" ? "" : vertical,
    limit: 50,
  });
  const publicLib = useHandLibrary(workspaceId, {
    scope: "public",
    search,
    vertical: vertical === "All" ? "" : vertical,
    limit: 50,
  });
  const companyHands = companyLib.data?.hands ?? [];
  const publicHands = publicLib.data?.hands ?? [];

  // Auto-select on open: if we seeded the search with the step's existing
  // agent name (templates carry name-not-id), and the filtered library
  // returns exactly one case-insensitive match, lock it in so the user
  // sees the row highlighted. Runs once — the ref guards against the user's
  // later edits stomping their selection.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!seededNameLookup) return;
    if (selection !== null) return;
    if (companyLib.isLoading || publicLib.isLoading) return;
    const target = currentOwner.trim().toLowerCase();
    const all = [...companyHands, ...publicHands];
    const matches = all.filter((h) => h.name.toLowerCase() === target);
    if (matches.length === 1) {
      const m = matches[0]!;
      setSelection({ kind: "hand", handId: m.id, name: m.name });
      autoSelectedRef.current = true;
    }
  }, [
    seededNameLookup, selection, currentOwner,
    companyHands, publicHands,
    companyLib.isLoading, publicLib.isLoading,
  ]);

  const VERTICALS = ["All", "General", "Wealth Management", "Healthcare", "Logistics", "IT Services & BPO", "Professional Services", "HR"];

  function handleSave() {
    if (mode === "staff") {
      onSave({ owner: staffName || "Operator", autonomous: false, handId: null, bindingId: null });
    } else if (selection?.kind === "binding") {
      onSave({ owner: selection.name, autonomous: true, handId: null, bindingId: selection.bindingId });
    } else if (selection?.kind === "hand") {
      onSave({ owner: selection.name, autonomous: true, handId: selection.handId, bindingId: null });
    }
  }

  const canSave = mode === "staff" ? staffName.trim().length > 0 : selection !== null;

  const SURFACE_ICONS: Record<string, string> = {
    m365: "🔵",
    ghl: "🟠",
    xero: "🟢",
    google: "🔴",
    slack: "🟣",
  };
  function surfaceIcon(surface: string) {
    for (const [k, icon] of Object.entries(SURFACE_ICONS)) {
      if (surface.toLowerCase().includes(k)) return icon;
    }
    return "🔗";
  }

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)",
          borderRadius: 10,
          width: 580,
          maxHeight: "84vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Edit step</span>
            <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--text-3)", lineHeight: 1 }}>×</button>
          </div>
          <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{stepTitle}</div>
        </div>

        {/* Mode toggle */}
        <div style={{ padding: "14px 20px 10px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Assigned to
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => setMode("staff")}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 7,
                border: `1.5px solid ${mode === "staff" ? "var(--accent)" : "var(--border)"}`,
                background: mode === "staff" ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--bg-soft)",
                color: mode === "staff" ? "var(--accent)" : "var(--text-2)",
                fontWeight: mode === "staff" ? 600 : 400,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              👤 Staff member
            </button>
            <button
              type="button"
              onClick={() => setMode("agent")}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 7,
                border: `1.5px solid ${mode === "agent" ? "var(--accent)" : "var(--border)"}`,
                background: mode === "agent" ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--bg-soft)",
                color: mode === "agent" ? "var(--accent)" : "var(--text-2)",
                fontWeight: mode === "agent" ? 600 : 400,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              ⚡ AI Agent
            </button>
          </div>
        </div>

        {/* No-LLM-key hint (agent mode only) */}
        {showNoKeyHint && (
          <div
            data-testid="sem-no-llm-hint"
            style={{
              padding: "8px 20px",
              fontSize: 12,
              color: "var(--text-2)",
              background: "color-mix(in srgb, var(--danger, #d9534f) 6%, transparent)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <strong>No LLM key configured.</strong>{" "}
            You can still assign an agent, but it won't run until a key is
            added in Hands Agents → Integration &amp; LLM.
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {mode === "staff" ? (
            <div style={{ padding: "14px 20px" }}>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8 }}>Select a team member or type a role</div>
              <input
                autoFocus
                type="text"
                placeholder="e.g. Head of CS, VP Sales…"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                  background: "var(--bg-soft)",
                  color: "var(--text)",
                  fontSize: 13,
                  boxSizing: "border-box",
                  marginBottom: 10,
                }}
              />
              {members.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {members.map((m) => (
                    <button
                      key={m.userId}
                      type="button"
                      onClick={() => setStaffName(m.displayName)}
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 6,
                        border: staffName === m.displayName ? "1px solid var(--accent)" : "1px solid transparent",
                        background: staffName === m.displayName ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--bg-soft)",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "var(--text)",
                      }}
                    >
                      <strong>{m.displayName}</strong>
                      <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-3)" }}>{m.role} · {m.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Sub-tabs */}
              <div style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "0 20px" }}>
                {([["library", "Xamun Agents"], ["connected", `Connected${bindings.length > 0 ? ` (${bindings.length})` : ""}`]] as [AgentTab, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setAgentTab(key)}
                    style={{
                      padding: "10px 14px",
                      background: "none",
                      border: "none",
                      borderBottom: agentTab === key ? "2px solid var(--accent)" : "2px solid transparent",
                      color: agentTab === key ? "var(--accent)" : "var(--text-3)",
                      fontWeight: agentTab === key ? 600 : 400,
                      fontSize: 13,
                      cursor: "pointer",
                      marginBottom: -1,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {agentTab === "library" ? (
                <div style={{ padding: "10px 0" }}>
                  {/* Search + vertical filter */}
                  <div style={{ padding: "4px 20px 6px" }}>
                    <input
                      type="search"
                      placeholder="Search agents…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        borderRadius: 6,
                        border: "1px solid var(--border)",
                        background: "var(--bg-soft)",
                        color: "var(--text)",
                        fontSize: 13,
                        boxSizing: "border-box",
                        marginBottom: 8,
                      }}
                    />
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {VERTICALS.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setVertical(v)}
                          style={{
                            padding: "3px 9px",
                            borderRadius: 10,
                            border: "1px solid var(--border)",
                            background: vertical === v ? "var(--accent)" : "var(--bg-soft)",
                            color: vertical === v ? "#fff" : "var(--text-2)",
                            fontSize: 11,
                            cursor: "pointer",
                            fontWeight: vertical === v ? 600 : 400,
                          }}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  {companyHands.length > 0 && (
                    <div style={{ padding: "6px 20px 4px" }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Company library</div>
                      {companyHands.map((h) => (
                        <AgentRow key={h.id} hand={h} selected={selection?.kind === "hand" && selection.handId === h.id} onSelect={(id, name) => setSelection({ kind: "hand", handId: id, name })} />
                      ))}
                    </div>
                  )}
                  {publicHands.length > 0 && (
                    <div style={{ padding: "4px 20px" }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Public library</div>
                      {publicHands.map((h) => (
                        <AgentRow key={h.id} hand={h} selected={selection?.kind === "hand" && selection.handId === h.id} onSelect={(id, name) => setSelection({ kind: "hand", handId: id, name })} />
                      ))}
                    </div>
                  )}
                  {companyHands.length === 0 && publicHands.length === 0 && !companyLib.isLoading && (
                    <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-3)", fontSize: 13 }}>
                      No agents found{search ? ` for "${search}"` : ""}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: "12px 20px" }}>
                  {bindings.length > 0 ? bindings.map((b) => (
                    <BindingRow
                      key={b.id}
                      binding={b}
                      icon={surfaceIcon(b.surface)}
                      selected={selection?.kind === "binding" && selection.bindingId === b.id}
                      onSelect={(id, name) => setSelection({ kind: "binding", bindingId: id, name })}
                    />
                  )) : (
                    <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-3)", fontSize: 13, lineHeight: 1.6 }}>
                      No integrations connected yet.<br />
                      <span style={{ color: "var(--text-2)" }}>Go to <strong>Hands Agents → Integration &amp; LLM</strong> to connect M365, GHL, Xero, and more.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            disabled={saving || !canSave}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function BindingRow({
  binding,
  icon,
  selected,
  onSelect,
}: {
  binding: AgentBinding;
  icon: string;
  selected: boolean;
  onSelect: (bindingId: string, name: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(binding.id, binding.agent_name)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "8px 10px",
        borderRadius: 6,
        border: selected ? "1px solid var(--accent)" : "1px solid transparent",
        background: selected ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
        cursor: "pointer",
        textAlign: "left",
        marginBottom: 2,
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{binding.agent_name}</span>
          {selected && <span style={{ fontSize: 10, color: "var(--accent)", fontWeight: 700 }}>● selected</span>}
          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: "var(--bg-soft)", color: "var(--text-3)", border: "1px solid var(--border)" }}>
            {binding.surface}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>
          Connected · bound by {binding.bound_by_user.display_name}
        </div>
      </div>
    </button>
  );
}

function AgentRow({
  hand,
  selected,
  onSelect,
}: {
  hand: { id: string; name: string; description: string; category: string; vertical: string; icon: string | null };
  selected: boolean;
  onSelect: (id: string, name: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(hand.id, hand.name)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        width: "100%",
        padding: "7px 10px",
        borderRadius: 6,
        border: selected ? "1px solid var(--accent)" : "1px solid transparent",
        background: selected ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
        cursor: "pointer",
        textAlign: "left",
        marginBottom: 2,
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{hand.icon ?? "🤖"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{hand.name}</span>
          {selected && <span style={{ fontSize: 10, color: "var(--accent)", fontWeight: 700 }}>● selected</span>}
          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: "var(--bg-soft)", color: "var(--text-3)", border: "1px solid var(--border)" }}>
            {hand.vertical !== "General" ? hand.vertical : hand.category}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {hand.description}
        </div>
      </div>
    </button>
  );
}
